import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Eye, Trash2, RefreshCw, CheckCircle2, Clock } from "lucide-react";
import { api, API, apiError, TOKEN_KEY } from "@/lib/api";

const STATUS_STYLE = {
  under_review: { label: "Under Review", icon: Clock, cls: "text-amber-600 bg-amber-50" },
  verified: { label: "Verified", icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50" },
  approved: { label: "Verified", icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50" },
  rejected: { label: "Rejected", icon: Trash2, cls: "text-red-600 bg-red-50" },
};

// Reusable KYC/document uploader backed by Emergent object storage (/files).
export function FileUpload({ purpose, label, value, onChange, accept = "image/*,application/pdf" }) {
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", purpose);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data);
      toast.success(`${label} uploaded`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!value) return;
    try { await api.delete(`/files/${value.id}`); onChange(null); } catch (e) { toast.error(apiError(e)); }
  };

  const setExpiry = async (date) => {
    if (!value) return;
    try { await api.patch(`/files/${value.id}`, { expiry_date: date }); onChange({ ...value, expiry_date: date }); } catch (e) { toast.error(apiError(e)); }
  };

  const preview = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    window.open(`${API}/files/${value.id}/download?auth=${token}`, "_blank");
  };

  const st = STATUS_STYLE[value?.doc_status] || STATUS_STYLE.under_review;
  const inputId = `file-${purpose.replace(/[^a-z0-9]/gi, "-")}`;

  return (
    <div className="border border-border rounded-xl p-3" data-testid={`upload-${purpose}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-ink">{label}</span>
        {value && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${st.cls}`}><st.icon size={12} /> {st.label}</span>}
      </div>
      {!value ? (
        <label htmlFor={inputId} className="mt-2 flex items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg py-3 text-sm text-ink-600 cursor-pointer hover:border-coral">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} {busy ? "Uploading..." : "Upload file"}
          <input id={inputId} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`upload-input-${purpose}`} />
        </label>
      ) : (
        <div className="mt-2 flex items-center justify-between bg-surface rounded-lg px-3 py-2">
          <span className="text-xs text-ink truncate max-w-[150px]">{value.original_filename}</span>
          <div className="flex items-center gap-2">
            <button onClick={preview} className="text-ink-600" data-testid={`preview-${purpose}`}><Eye size={16} /></button>
            <label htmlFor={inputId} className="text-ink-600 cursor-pointer"><RefreshCw size={15} /><input id={inputId} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} /></label>
            <button onClick={remove} className="text-red-500" data-testid={`delete-${purpose}`}><Trash2 size={15} /></button>
          </div>
        </div>
      )}
      {value && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[11px] text-ink-600">Expiry date (if applicable)</span>
          <input type="date" value={(value.expiry_date || "").slice(0, 10)} onChange={(e) => setExpiry(e.target.value)} className="bg-surface rounded-lg px-2 py-1 text-xs outline-none" data-testid={`expiry-${purpose}`} />
        </div>
      )}
    </div>
  );
}
